import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';
import { MdClose, MdDownload } from 'react-icons/md';
import useSWR from 'swr';
import { toast } from 'App';
import { Button } from 'components/button';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { formatTokenAmount } from 'utils/amountUtils';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import {
  EInvoicePaymentStatus,
  IInvoicePaymentDetail,
  INVOICE_STATUS_LABELS,
} from 'views/types/invoicePayments';
import { statusPillClass } from './statusPill';

const S = EInvoicePaymentStatus;

type TReasonAction = 'reject' | 'request-docs' | 'cancel' | 'fail';

interface IProps {
  requestId: string;
  isOpen: boolean;
  onClose: () => void;
  refetchList: () => Promise<unknown>;
}

const sectionTitle =
  'mb-2 mt-4 text-sm font-bold uppercase text-gray-600 dark:text-white';
const rowLabel = 'w-52 shrink-0 text-sm text-gray-500';
const rowValue = 'text-sm text-navy-700 dark:text-white break-all';

const Row = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="flex gap-2 py-0.5">
    <span className={rowLabel}>{label}</span>
    <span className={rowValue}>{value ?? '—'}</span>
  </div>
);

const CRYPTO_DECIMALS: Record<string, number> = { USDT: 6, USDC: 6 };

export const RequestDialog: React.FC<IProps> = ({
  requestId,
  isOpen,
  onClose,
  refetchList,
}) => {
  const { canUpdate } = usePermissions();
  const [busy, setBusy] = useState<string | null>(null);
  const [reasonAction, setReasonAction] = useState<TReasonAction | null>(null);
  const [reasonText, setReasonText] = useState('');
  const proofInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, mutate } = useSWR(
    ['/api/admin/invoice-payments', requestId],
    () => invoicePaymentsService.getById(requestId)
  );

  const request: IInvoicePaymentDetail | undefined = data;
  const status = request?.status;

  const refresh = async () => {
    await Promise.all([mutate(), refetchList()]);
  };

  const runAction = async (
    key: string,
    resource: string,
    action: () => Promise<unknown>,
    successMessage: string
  ) => {
    if (!canUpdate(resource)) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission for this action',
        status: 'error',
      });
      return;
    }
    try {
      setBusy(key);
      await action();
      toast({
        title: 'Success',
        description: successMessage,
        status: 'success',
      });
      setReasonAction(null);
      setReasonText('');
      await refresh();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(null);
    }
  };

  const submitReasonAction = () => {
    if (!reasonAction) return;
    const id = requestId;
    if (reasonAction === 'reject') {
      if (!reasonText.trim()) {
        toast({ title: 'Reason is required', status: 'error' });
        return;
      }
      runAction(
        'reject',
        PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
        () => invoicePaymentsService.reject(id, reasonText.trim()),
        'Request rejected, reserved funds returned'
      );
    } else if (reasonAction === 'request-docs') {
      if (!reasonText.trim()) {
        toast({ title: 'Note is required', status: 'error' });
        return;
      }
      runAction(
        'request-docs',
        PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
        () => invoicePaymentsService.requestDocuments(id, reasonText.trim()),
        'Additional documents requested'
      );
    } else if (reasonAction === 'cancel') {
      runAction(
        'cancel',
        PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
        () => invoicePaymentsService.cancel(id, reasonText.trim() || undefined),
        'Request cancelled, reserved funds returned'
      );
    } else if (reasonAction === 'fail') {
      if (!reasonText.trim()) {
        toast({ title: 'Reason is required', status: 'error' });
        return;
      }
      runAction(
        'fail',
        PERMISSION_RESOURCES.INVOICE_FINANCE,
        () => invoicePaymentsService.markFailed(id, reasonText.trim()),
        'Marked as failed (funds stay frozen)'
      );
    }
  };

  const onProofSelected = async (file?: File | null) => {
    if (!file) return;
    await runAction(
      'proof',
      PERMISSION_RESOURCES.INVOICE_FINANCE,
      () => invoicePaymentsService.uploadProof(requestId, file),
      'Proof of Payment uploaded'
    );
    if (proofInputRef.current) proofInputRef.current.value = '';
  };

  const hasProof = !!request?.documents?.some(
    doc => doc.document_type === 'proof_of_payment'
  );

  const decimals = CRYPTO_DECIMALS[request?.crypto_asset ?? ''] ?? 6;

  const actionButton = (
    key: string,
    label: string,
    resource: string,
    onClick: () => void,
    tone: 'green' | 'red' | 'blue' | 'gray' = 'blue',
    disabled = false
  ) => {
    const toneCls =
      tone === 'green'
        ? 'bg-green-600 hover:bg-green-700'
        : tone === 'red'
          ? 'bg-red-600 hover:bg-red-700'
          : tone === 'gray'
            ? 'bg-gray-500 hover:bg-gray-600'
            : 'bg-brand-500 hover:bg-brand-600';
    return (
      <ReadPermissionGate key={key} resource={resource}>
        <button
          disabled={disabled || busy !== null || !canUpdate(resource)}
          onClick={onClick}
          className={`rounded px-3 py-2 text-sm font-medium text-white disabled:opacity-50 ${toneCls}`}
        >
          {busy === key ? 'Working…' : label}
        </button>
      </ReadPermissionGate>
    );
  };

  const renderActions = () => {
    if (!request || !status) return null;
    const buttons: React.ReactNode[] = [];

    if (status === S.UNDER_REVIEW || status === S.MORE_DOCUMENTS_REQUESTED) {
      buttons.push(
        actionButton(
          'approve',
          'Approve',
          PERMISSION_RESOURCES.INVOICE_APPROVE_CTA,
          () =>
            runAction(
              'approve',
              PERMISSION_RESOURCES.INVOICE_APPROVE_CTA,
              () => invoicePaymentsService.approve(requestId),
              'Request approved — moved to the finance queue'
            ),
          'green'
        ),
        actionButton(
          'reject',
          'Reject…',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () => setReasonAction('reject'),
          'red'
        ),
        actionButton(
          'request-docs',
          'Request Documents…',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () => setReasonAction('request-docs'),
          'gray'
        )
      );
    }
    if (status === S.MORE_DOCUMENTS_REQUESTED) {
      buttons.push(
        actionButton(
          'return',
          'Return to Review',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () =>
            runAction(
              'return',
              PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
              () => invoicePaymentsService.returnToReview(requestId),
              'Returned to review'
            ),
          'gray'
        )
      );
    }
    if (status === S.APPROVED) {
      buttons.push(
        actionButton(
          'processing',
          'Start Execution (Processing)',
          PERMISSION_RESOURCES.INVOICE_FINANCE,
          () =>
            runAction(
              'processing',
              PERMISSION_RESOURCES.INVOICE_FINANCE,
              () => invoicePaymentsService.moveToProcessing(requestId),
              'Moved to Processing'
            ),
          'blue'
        ),
        actionButton(
          'reject',
          'Reject…',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () => setReasonAction('reject'),
          'red'
        )
      );
    }
    if (status === S.PROCESSING) {
      buttons.push(
        actionButton(
          'proof',
          hasProof ? 'Replace Proof of Payment…' : 'Upload Proof of Payment…',
          PERMISSION_RESOURCES.INVOICE_FINANCE,
          () => proofInputRef.current?.click(),
          'blue'
        ),
        actionButton(
          'complete',
          'Complete (settle funds)',
          PERMISSION_RESOURCES.INVOICE_FINANCE,
          () =>
            runAction(
              'complete',
              PERMISSION_RESOURCES.INVOICE_FINANCE,
              () => invoicePaymentsService.complete(requestId),
              'Completed — crypto permanently debited'
            ),
          'green',
          !hasProof
        ),
        actionButton(
          'fail',
          'Mark Failed…',
          PERMISSION_RESOURCES.INVOICE_FINANCE,
          () => setReasonAction('fail'),
          'red'
        )
      );
    }
    if (status === S.FAILED) {
      buttons.push(
        actionButton(
          'retry',
          'Retry Execution',
          PERMISSION_RESOURCES.INVOICE_FINANCE,
          () =>
            runAction(
              'retry',
              PERMISSION_RESOURCES.INVOICE_FINANCE,
              () => invoicePaymentsService.retry(requestId),
              'Back to Processing'
            ),
          'blue'
        ),
        actionButton(
          'reject',
          'Reject & Refund…',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () => setReasonAction('reject'),
          'red'
        )
      );
    }
    if (
      status !== S.COMPLETED &&
      status !== S.REJECTED &&
      status !== S.CANCELLED &&
      status !== S.PROCESSING &&
      status !== S.FAILED
    ) {
      buttons.push(
        actionButton(
          'cancel',
          'Cancel…',
          PERMISSION_RESOURCES.INVOICE_REJECT_CTA,
          () => setReasonAction('cancel'),
          'gray'
        )
      );
    }
    return buttons;
  };

  return (
    <ReadPermissionGate resource={PERMISSION_RESOURCES.INVOICE_INNER_PAGE}>
      <Dialog.Root
        open={isOpen}
        onOpenChange={open => !open && onClose()}
        modal
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
          <Dialog.Content
            onInteractOutside={e => {
              if ((e.target as HTMLElement).closest('.chakra-toast')) {
                e.preventDefault();
              }
            }}
            className="fixed left-[50%] top-[50%] z-[60] max-h-[90vh] w-[92vw] max-w-[980px] translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-[6px] bg-white p-6 shadow-xl focus:outline-none dark:bg-gray-800 dark:text-white"
          >
            <Dialog.Title className="mb-4 flex items-center justify-between text-lg font-bold">
              <span>
                Invoice Payment{' '}
                <span className="font-mono">
                  {request?.invoice_payment_number ?? ''}
                </span>{' '}
                {status && (
                  <span
                    className={`ml-2 rounded px-2 py-0.5 text-xs font-medium ${statusPillClass(
                      status
                    )}`}
                  >
                    {INVOICE_STATUS_LABELS[status] ?? status}
                  </span>
                )}
              </span>
              <button onClick={onClose} aria-label="Close">
                <MdClose className="h-6 w-6" />
              </button>
            </Dialog.Title>

            {isLoading && <div className="py-10 text-center">Loading…</div>}

            {request && (
              <div>
                <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                  <div>
                    <div className={sectionTitle}>Client</div>
                    <Row
                      label="Coinsfly ID"
                      value={request.client?.coinsFlyId}
                    />
                    <Row label="Name" value={request.client?.name} />
                    <Row label="Email" value={request.client_email} />
                    <Row
                      label="Client type"
                      value={request.client?.clientType}
                    />
                    <Row label="KYB status" value={request.client?.kybStatus} />
                    <Row
                      label="Invoice Payment enabled"
                      value={String(
                        request.client?.invoicePaymentEnabled ?? '—'
                      )}
                    />

                    <div className={sectionTitle}>Payment</div>
                    <Row
                      label="Beneficiary gets"
                      value={`${request.fiat_amount} ${request.fiat_currency}`}
                    />
                    <Row
                      label="Client paid"
                      value={`${formatTokenAmount(
                        request.crypto_amount_reserved,
                        decimals
                      )} ${request.crypto_asset}`}
                    />
                    <Row
                      label="Effective rate"
                      value={`${request.exchange_rate} ${request.crypto_asset}/${request.fiat_currency}`}
                    />
                    <Row
                      label="Fee (internal)"
                      value={`${request.fee_percentage}% (${request.fee_amount_crypto} ${request.crypto_asset})`}
                    />
                    <Row
                      label="EUR equivalent"
                      value={`${request.eur_equivalent} EUR`}
                    />
                    <Row label="Payment method" value={request.rail} />
                    <Row label="Purpose" value={request.payment_purpose} />
                    <Row label="Sector" value={request.beneficiary_sector} />
                    <Row label="Invoice No." value={request.invoice_number} />
                    <Row label="Reference" value={request.payment_reference} />

                    <div className={sectionTitle}>Risk Flags</div>
                    <div className="flex flex-wrap gap-1">
                      {request.risk_flags?.length ? (
                        request.risk_flags.map(flag => (
                          <span
                            key={flag}
                            className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          >
                            {flag}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className={sectionTitle}>Beneficiary</div>
                    <Row
                      label="Name"
                      value={request.beneficiary?.beneficiary_name}
                    />
                    <Row
                      label="Registration No."
                      value={request.beneficiary?.registration_number}
                    />
                    <Row
                      label="Address"
                      value={[
                        request.beneficiary?.address_number,
                        request.beneficiary?.street,
                        request.beneficiary?.city,
                        request.beneficiary?.town_state_province,
                        request.beneficiary?.zip_postcode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    />
                    <Row label="Country" value={request.beneficiary_country} />
                    <Row
                      label="Website"
                      value={request.beneficiary?.website_url}
                    />
                    <Row
                      label="Transfer reason"
                      value={request.beneficiary?.transfer_reason}
                    />

                    <div className={sectionTitle}>Bank Details</div>
                    <Row label="Bank" value={request.bank_details?.bank_name} />
                    <Row
                      label="Bank country"
                      value={request.bank_details?.bank_country}
                    />
                    <Row label="IBAN" value={request.bank_details?.iban} />
                    <Row
                      label="Account No."
                      value={request.bank_details?.account_number}
                    />
                    <Row
                      label="Sort code"
                      value={request.bank_details?.sort_code}
                    />
                    <Row
                      label="Routing No. (ABA)"
                      value={request.bank_details?.routing_number}
                    />
                    <Row
                      label="BIC/SWIFT"
                      value={request.bank_details?.bic_swift}
                    />
                    <Row
                      label="Bank address"
                      value={request.bank_details?.bank_address}
                    />

                    <div className={sectionTitle}>Timeline</div>
                    <Row
                      label="Submitted"
                      value={new Date(request.created_at).toLocaleString()}
                    />
                    {request.reviewed_at && (
                      <Row
                        label="Reviewed"
                        value={new Date(request.reviewed_at).toLocaleString()}
                      />
                    )}
                    {request.approved_at && (
                      <Row
                        label="Approved"
                        value={new Date(request.approved_at).toLocaleString()}
                      />
                    )}
                    {request.processing_started_at && (
                      <Row
                        label="Processing started"
                        value={new Date(
                          request.processing_started_at
                        ).toLocaleString()}
                      />
                    )}
                    {request.completed_at && (
                      <Row
                        label="Completed"
                        value={new Date(request.completed_at).toLocaleString()}
                      />
                    )}
                    {request.rejected_at && (
                      <Row
                        label="Rejected"
                        value={new Date(request.rejected_at).toLocaleString()}
                      />
                    )}
                    {request.cancelled_at && (
                      <Row
                        label="Cancelled"
                        value={new Date(request.cancelled_at).toLocaleString()}
                      />
                    )}
                    {request.failed_at && (
                      <Row
                        label="Failed"
                        value={new Date(request.failed_at).toLocaleString()}
                      />
                    )}
                    <Row
                      label="Funds"
                      value={
                        request.balance_settled_at
                          ? 'Settled (permanently debited)'
                          : request.balance_refunded_at
                            ? 'Refunded to client'
                            : request.balance_deducted_at
                              ? 'Frozen (reserved)'
                              : 'Not reserved'
                      }
                    />

                    {(request.transfi_order_id ||
                      request.transfi_status ||
                      request.client?.transfiUserId) && (
                      <>
                        <div className={sectionTitle}>TransFi Execution</div>
                        <Row
                          label="Order ID"
                          value={request.transfi_order_id ?? '-'}
                        />
                        <Row
                          label="Provider status"
                          value={request.transfi_status ?? '-'}
                        />
                        {request.transfi_conversion_rate && (
                          <Row
                            label="Conversion rate"
                            value={request.transfi_conversion_rate}
                          />
                        )}
                        {request.transfi_invoice_id && (
                          <Row
                            label="Invoice (TransFi)"
                            value={request.transfi_invoice_id}
                          />
                        )}
                        {request.executed_at && (
                          <Row
                            label="Executed"
                            value={new Date(
                              request.executed_at
                            ).toLocaleString()}
                          />
                        )}
                        {request.transfi_failure_code && (
                          <Row
                            label="Provider failure"
                            value={`${request.transfi_failure_code}${
                              request.transfi_failure_message
                                ? ` - ${request.transfi_failure_message}`
                                : ''
                            }`}
                          />
                        )}
                        {request.transfi_status === 'kyb_required' && (
                          <Row
                            label="KYB escalation"
                            value="Waiting for the client to complete TransFi business verification"
                          />
                        )}
                        {request.client?.transfiUserId && (
                          <Row
                            label="TransFi user"
                            value={`${request.client.transfiUserId}${
                              request.client.transfiKybStatus
                                ? ` (${request.client.transfiKybStatus})`
                                : ''
                            }`}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {(request.rejection_reason ||
                  request.more_documents_note ||
                  request.failure_reason) && (
                  <div>
                    <div className={sectionTitle}>Notes</div>
                    {request.rejection_reason && (
                      <Row
                        label="Rejection reason"
                        value={request.rejection_reason}
                      />
                    )}
                    {request.more_documents_note && (
                      <Row
                        label="Documents requested"
                        value={request.more_documents_note}
                      />
                    )}
                    {request.failure_reason && (
                      <Row
                        label="Failure reason"
                        value={request.failure_reason}
                      />
                    )}
                  </div>
                )}

                <div className={sectionTitle}>Documents</div>
                <div className="flex flex-col gap-1">
                  {request.documents?.length ? (
                    request.documents.map(doc => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-3 text-sm"
                      >
                        <span className="w-40 text-gray-500">
                          {doc.document_type}
                        </span>
                        <span className="break-all">{doc.file_name}</span>
                        <button
                          className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs hover:bg-gray-50 dark:hover:bg-navy-700"
                          onClick={() =>
                            invoicePaymentsService.downloadDocument(
                              request.id,
                              doc.id,
                              doc.file_name
                            )
                          }
                        >
                          <MdDownload /> Download
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No documents</span>
                  )}
                </div>

                {/* Reason input for reject / request-docs / cancel / fail */}
                {reasonAction && (
                  <div className="mt-4 rounded border border-gray-200 p-3 dark:border-white/20">
                    <div className="mb-2 text-sm font-medium">
                      {reasonAction === 'reject' &&
                        'Rejection reason (shown to the client):'}
                      {reasonAction === 'request-docs' &&
                        'Which documents are required (shown to the client):'}
                      {reasonAction === 'cancel' &&
                        'Cancellation reason (optional):'}
                      {reasonAction === 'fail' &&
                        'Failure reason (internal, funds stay frozen):'}
                    </div>
                    <textarea
                      className="w-full rounded-md border border-gray-200 bg-white p-2 text-sm dark:bg-navy-800 dark:text-white"
                      rows={3}
                      value={reasonText}
                      onChange={e => setReasonText(e.target.value)}
                    />
                    <div className="mt-2 flex gap-2">
                      <Button
                        onClick={submitReasonAction}
                        disabled={busy !== null}
                      >
                        Confirm
                      </Button>
                      <button
                        className="rounded border border-gray-200 px-3 py-2 text-sm dark:text-white"
                        onClick={() => {
                          setReasonAction(null);
                          setReasonText('');
                        }}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {!reasonAction && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {renderActions()}
                  </div>
                )}
                {status === S.PROCESSING && !hasProof && (
                  <div className="mt-2 text-xs text-amber-600">
                    Complete is locked until a Proof of Payment document is
                    uploaded.
                  </div>
                )}

                <input
                  ref={proofInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg"
                  className="hidden"
                  onChange={e => onProofSelected(e.target.files?.[0])}
                />
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ReadPermissionGate>
  );
};
